<template>
  <!-- dragover/drop.prevent keep a photo dropped between rows from opening in the browser. -->
  <section ref="sectionRef" class="location-uploads" @dragover.prevent @drop.prevent>
    <div class="location-uploads__heading">
      <div>
        <h2 class="global-h2 location-uploads__title">Upload by location</h2>
        <p class="location-uploads__subtitle">Drop photos onto a location, or use Upload Photos.</p>
      </div>
      <SrpButton class="location-uploads__new-album" tag="button" type="button" color="gray" fill="outlined" sidePaddings="small" @click="openAddModal('Location')">
        <template #icon><IconEmbedded name="plus_2-5" :size="16" /></template>
        New Album
      </SrpButton>
    </div>

    <div class="location-uploads__toolbar">
      <div class="location-uploads__search">
        <IconEmbedded name="magnifier_2-5" :size="16" color="#667085" />
        <input v-model="search" type="text" aria-label="Search" placeholder="Search" />
      </div>
      <LocationCategoryDropdown v-model="selectedCategory" class="location-uploads__category" allLabel="All categories" />

      <SrpDropdown class="location-uploads__photos-filter" placement="bottom-start" :offset="4" :width="170">
        <template #clickableElement="{ isDropdownVisible }">
          <button type="button" class="location-uploads__filter-trigger" :aria-label="`Filter by photos: ${photosFilterLabel}`" :aria-expanded="isDropdownVisible">
            <span>{{ photosFilterLabel }}</span>
            <IconEmbedded name="caret-bottom_3" :size="14" color="#667085" />
          </button>
        </template>
        <template #dropdown="{ close, isDropdownVisible }">
          <div class="location-uploads__filter-menu" :inert="!isDropdownVisible" :aria-hidden="!isDropdownVisible" @keydown.esc.stop="close()">
            <button
              v-for="option in PHOTOS_FILTER_OPTIONS"
              :key="option.value"
              type="button"
              :class="{ 'location-uploads__filter-choice--selected': photosFilter === option.value }"
              @click="choosePhotosFilter(option.value, close)"
            >
              {{ option.label }}
            </button>
          </div>
        </template>
      </SrpDropdown>

      <LinkWithIcon class="location-uploads__sort" isDottedUnderline iconInset="2px auto auto 2px" color="gray" @click="reverseSort">
        <template #icon><IconEmbedded :name="isNewestFirst ? 'descending_3' : 'ascending_3'" :size="15" /></template>
        {{ isNewestFirst ? "newest" : "oldest" }} first
      </LinkWithIcon>
    </div>

    <div class="location-uploads__rows-wrap">
      <ul v-if="renderedRows.length" class="location-uploads__rows">
        <LocationUploadRow
          v-for="row in renderedRows"
          :key="row.key"
          :data-row-key="row.key"
          :row="row"
          :customerId="props.customerId"
          :saveFunction="assetIds => savePhotos(row.key, assetIds)"
          :checkedPhotoIds="checkedPhotoIdsFor(row)"
          :isSelectMode="isSelectMode"
          :areAlbumActionsDisabled="isAnyUploading"
          :isVideoUploadAllowed="props.isVideoUploadAllowed"
          @uploadStateChanged="isUploading => handleUploadStateChanged(row.key, isUploading)"
          @checkUncheckFile="handleCheckUncheckFile"
          @checkUncheckAll="isChecked => handleCheckUncheckAll(row, isChecked)"
          @startSelecting="startSelecting"
          @stopSelecting="stopSelecting"
          @editAlbum="withLocationPicker => openAlbumEditor(row.gallery, withLocationPicker)"
          @deleteAlbum="handleDeleteAlbum(row)"
        />
      </ul>

      <div v-else class="location-uploads__empty">
        <p class="location-uploads__empty-text">{{ emptyText }}</p>
        <div class="location-uploads__empty-actions">
          <SrpButton tag="button" type="button" color="teal" fill="outlined" size="small" @click="openAddModal('Location')">
            <template #icon><IconEmbedded name="plus_2-5" :size="16" /></template>
            Add a location
          </SrpButton>
          <SrpButton tag="button" type="button" color="gray" fill="outlined" size="small" @click="openAddModal('Album')">
            <template #icon><IconEmbedded name="plus_2-5" :size="16" /></template>
            New Album
          </SrpButton>
        </div>
      </div>

      <div v-if="hasMoreRows" ref="loadMoreSentinel" class="location-uploads__more">Loading more...</div>

      <SelectedFilesPanel
        v-if="isSelectMode && checkedPhotoIds.length"
        class="location-uploads__selected-files-panel"
        :selectedFiles="selectedFiles"
        :isPreviewLinkVisible="false"
        :isDragAndDropOn="false"
        @remove="photoId => handleCheckUncheckFile(photoId, false)"
      >
        <template #headerRightSide>
          <LinkWithIcon isDottedUnderline color="teal" iconInset="auto auto auto 2px" @click="stopSelecting">
            <template #icon><IconEmbedded name="remove_3-5" :size="12" /></template>
            Cancel
          </LinkWithIcon>

          <LinkWithIcon isDottedUnderline color="red" iconInset="auto auto auto 2px" @click="isDeletePhotosModalVisible = true">
            <template #icon><IconEmbedded name="trashcan-alt_2-5" :size="17" /></template>
            {{ isCompactScreen ? "Delete" : "Delete selected" }}
          </LinkWithIcon>

          <LinkWithIcon isDottedUnderline color="teal" iconInset="auto auto auto 2px" @click="isMovePhotosModalVisible = true">
            <template #icon><IconEmbedded name="arrow-corner-top-right_2-5" :size="16" /></template>
            {{ isCompactScreen ? "Move" : "Move selected to an album" }}
          </LinkWithIcon>
        </template>
      </SelectedFilesPanel>
    </div>

    <DeletePhotosConfirmationModal
      v-if="isDeletePhotosModalVisible"
      v-model:isVisible="isDeletePhotosModalVisible"
      :customerId="props.customerId"
      :fileIdsToDelete="checkedPhotoIds"
      :currentOperationSource="props.currentOperationSource"
      @deletionCompleted="handlePhotosDeleted"
    />

    <MovePhotosToAGalleryModal
      v-if="isMovePhotosModalVisible"
      v-model:isVisible="isMovePhotosModalVisible"
      :photoIds="checkedPhotoIds"
      :customerId="props.customerId"
      :allGroups="moveTargets"
      groupNoun="album"
      :resolveGalleryId="resolveMoveTarget"
      @completeMoving="handlePhotosMoved"
    />

    <AddLocationOrAlbumModal
      v-model:isVisible="isAddModalVisible"
      :customerId="props.customerId"
      :initialType="addModalInitialType"
      :currentOperationSource="props.currentOperationSource ?? undefined"
      @locationAdded="handleLocationAdded"
      @albumCreated="handleAlbumCreated"
    />

    <EditTitleAndCommentModal
      v-if="isAlbumEditorVisible && albumBeingEdited"
      v-model:isVisible="isAlbumEditorVisible"
      :customerId="props.customerId"
      :photoGalleryId="albumBeingEdited.id"
      :title="albumBeingEdited.groupName"
      :comment="albumBeingEdited.comment"
      :photoCredit="albumBeingEdited.photoCredit ?? null"
      :assignedLocationId="albumBeingEdited.locationId ?? null"
      :shouldOpenWithLocationPicker="shouldOpenAlbumEditorWithLocationPicker"
      :currentOperationSource="props.currentOperationSource ?? undefined"
      groupNoun="album"
      @save="handleAlbumSaved"
    />

    <DeleteGroupConfirmationModal
      v-if="isDeleteAlbumModalVisible"
      v-model:isVisible="isDeleteAlbumModalVisible"
      :customerId="props.customerId"
      :groupId="albumBeingDeleted?.id"
      :allGroupFiles="albumBeingDeleted?.photoIds ?? []"
      :currentOperationSource="props.currentOperationSource"
      groupNoun="album"
      @deletionCompleted="removeGallery(albumBeingDeleted?.id)"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type { Ref } from "vue";
import { MetricSender } from "@helpers/MetricSender";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import { isScreenEqualOrSmaller } from "@helpers/ScreenSizeHelper";
import { buildLocationUploadRows, orderRows, rowLocationLabel, rowMatchesFilters, sortRows } from "@logic/LocationUploadRows";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import PhotoGalleryV2Repo from "@repos/PhotoGalleryV2Repo";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import SelectedFilesPanel from "@components/SelectedFilesPanel.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import DeleteGroupConfirmationModal from "@components/AllPhotosSection/DeleteGroupConfirmationModal.vue";
import DeletePhotosConfirmationModal from "@components/AllPhotosSection/DeletePhotosConfirmationModal.vue";
import EditTitleAndCommentModal from "@components/AllPhotosSection/EditTitleAndCommentModal.vue";
import MovePhotosToAGalleryModal from "@components/AllPhotosSection/MovePhotosToAGalleryModal.vue";
import LocationCategoryDropdown from "@components/SocialLocations/LocationCategoryDropdown.vue";
import AddLocationOrAlbumModal from "./AddLocationOrAlbumModal.vue";
import LocationUploadRow from "./LocationUploadRow.vue";

// Types
import type { AddLocationOrAlbumType } from "./AddLocationOrAlbumModal.vue";
import type { LocationUploadRow as LocationUploadRowData, PhotosFilter, RowFilters } from "@logic/LocationUploadRows";
import type { PhotoGalleryCustomerData, PhotoGallerySource } from "@contracts/photosPageData";
import type { LocationData } from "@contracts/socialLocations";
import type { ScreenSize } from "@contracts/screenSize";
import type { UploadedImage } from "@contracts/uploadedImage";

// Each row carries an upload form, so only this many are added to the page at a time.
const ROWS_PER_PAGE = 30;
const NO_PHOTO_IDS: Array<string> = [];

const props = withDefaults(
  defineProps<{
    customerId: string;
    /** Starting lists. After mount this section owns its own copies and reports gallery changes through galleryGroupsUpdated. */
    galleryGroups: Array<PhotoGalleryCustomerData>;
    locations: Array<LocationData>;
    currentOperationSource?: PhotoGallerySource | null;
    isVideoUploadAllowed?: boolean;
  }>(),
  {
    currentOperationSource: null,
    isVideoUploadAllowed: false,
  }
);

const emit = defineEmits<{
  (e: "galleryGroupsUpdated", galleryGroups: Array<PhotoGalleryCustomerData>): void;
  (e: "anyUploadingChanged", isAnyUploading: boolean): void;
}>();

const screenSize = inject("screenSize") as Ref<ScreenSize>;
const isCompactScreen = computed<boolean>(() => isScreenEqualOrSmaller(screenSize.value, "tablet"));

// Data ======================================================================
const galleries = ref<Array<PhotoGalleryCustomerData>>([...(props.galleryGroups ?? [])]);
const locationList = ref<Array<LocationData>>([...(props.locations ?? [])]);

function emitGalleries() {
  emit("galleryGroupsUpdated", [...galleries.value]);
}

function upsertGallery(gallery: PhotoGalleryCustomerData) {
  const index = galleries.value.findIndex(g => g.id === gallery.id);
  if (index === -1) galleries.value.push(gallery);
  else galleries.value.splice(index, 1, gallery);
  emitGalleries();
}

function removeGallery(galleryId: string | null | undefined) {
  if (!galleryId) return;
  galleries.value = galleries.value.filter(g => g.id !== galleryId);
  emitGalleries();
}

async function reloadGalleries() {
  galleries.value = await ContentCalendarRepo.loadUploadGroups(props.customerId);
  emitGalleries();
}

async function reloadLocations() {
  try {
    locationList.value = await SocialLocationsRepo.list(props.customerId, false, false);
  } catch (error) {
    // Non-blocking: the rows already on the page keep working with the locations we have.
    getGlobalRemoteLogger()?.error(`LocationUploadsSection.reloadLocations failed. customerId=${props.customerId} error=${error}`);
  }
}

function upsertLocation(location: LocationData) {
  locationList.value = [location, ...locationList.value.filter(l => l.id !== location.id)];
}

// Rows ======================================================================
// See LocationUploadRows.ts for how rows, their keys and their order are decided.
const isNewestFirst = ref<boolean>(true);
const builtRows = computed<Array<LocationUploadRowData>>(() => sortRows(buildLocationUploadRows(galleries.value, locationList.value), isNewestFirst.value));
const rowOrder = ref<Array<string>>([]);
const orderedRows = computed<Array<LocationUploadRowData>>(() => orderRows(builtRows.value, rowOrder.value));

function reverseSort() {
  isNewestFirst.value = !isNewestFirst.value;
  // Forget the order on screen so every row is placed by the new sort (rows otherwise keep their place).
  rowOrder.value = [];
}
const rowsByKey = computed<Map<string, LocationUploadRowData>>(() => new Map(orderedRows.value.map(row => [row.key, row])));

// Remember what was shown so the next change keeps every row where it is.
watch(
  orderedRows,
  rows => {
    const keys = rows.map(row => row.key);
    if (keys.length !== rowOrder.value.length || keys.some((key, index) => key !== rowOrder.value[index])) rowOrder.value = keys;
  },
  { immediate: true }
);

// Uploads ===================================================================
const uploadingRowKeys = ref<Set<string>>(new Set());
const isAnyUploading = computed<boolean>(() => uploadingRowKeys.value.size > 0);

function handleUploadStateChanged(rowKey: string, isUploading: boolean) {
  const wasAnyUploading = isAnyUploading.value;
  if (isUploading) uploadingRowKeys.value.add(rowKey);
  else uploadingRowKeys.value.delete(rowKey);
  if (wasAnyUploading !== isAnyUploading.value) emit("anyUploadingChanged", isAnyUploading.value);
}

// A row's saves arrive in several batches. They all wait on the same request, so one location only ever asks once.
const ensureRequests = new Map<string, Promise<PhotoGalleryCustomerData>>();

/** The gallery behind a row, created on the server first when the row is a location that doesn't have one yet. */
async function ensureGalleryForRow(rowKey: string): Promise<PhotoGalleryCustomerData> {
  // Looked up by key at call time: the row object a caller holds may predate the gallery being attached to it.
  const row = rowsByKey.value.get(rowKey);
  if (!row) throw new Error(`LocationUploadsSection: no row for ${rowKey}`);
  if (row.gallery) return row.gallery;

  const locationId = row.location.id;
  let request = ensureRequests.get(locationId);
  if (!request) {
    request = PhotoGalleryV2Repo.ensureGroupForLocation(props.customerId, locationId, props.currentOperationSource)
      .then(gallery => {
        upsertGallery(gallery);
        MetricSender.sendMetric("Cust-Calendar-Uploads-LocationAlbumStarted", { customerId: props.customerId, locationId });
        return gallery;
      })
      // Cleared either way: on success the row now has its gallery, on failure the next batch should ask again.
      .finally(() => ensureRequests.delete(locationId));
    ensureRequests.set(locationId, request);
  }
  return request;
}

/** assetIds are newest first, the same order the server stores them in. */
async function savePhotos(rowKey: string, assetIds: Array<string>) {
  const gallery = await ensureGalleryForRow(rowKey);
  await PhotoGalleryV2Repo.addPhotosToGroup(props.customerId, gallery.id, assetIds, props.currentOperationSource);

  const savedGallery = galleries.value.find(g => g.id === gallery.id);
  if (savedGallery) {
    const added = new Set(assetIds);
    savedGallery.photoIds = [...assetIds, ...(savedGallery.photoIds ?? []).filter(photoId => !added.has(photoId))];
  }
  emitGalleries();
}

function handleBeforeUnload(event: BeforeUnloadEvent) {
  if (!isAnyUploading.value) return;
  event.preventDefault();
  event.returnValue = "";
}

onMounted(() => window.addEventListener("beforeunload", handleBeforeUnload));
onBeforeUnmount(() => window.removeEventListener("beforeunload", handleBeforeUnload));

// Filters and paging ========================================================
const PHOTOS_FILTER_OPTIONS: Array<{ value: PhotosFilter; label: string }> = [
  { value: "all", label: "All albums" },
  { value: "withPhotos", label: "With photos" },
  { value: "withoutPhotos", label: "Without photos" },
];

const search = ref<string>("");
const selectedCategory = ref<string>("");
const photosFilter = ref<PhotosFilter>("all");
const visibleRowCount = ref<number>(ROWS_PER_PAGE);

const photosFilterLabel = computed<string>(() => PHOTOS_FILTER_OPTIONS.find(option => option.value === photosFilter.value)?.label ?? "");

function choosePhotosFilter(value: PhotosFilter, close: () => void) {
  photosFilter.value = value;
  close();
}

const filters = computed<RowFilters>(() => ({ query: search.value, category: selectedCategory.value, photos: photosFilter.value }));
const matchingRows = computed<Array<LocationUploadRowData>>(() => orderedRows.value.filter(row => rowMatchesFilters(row, filters.value)));
const hasMoreRows = computed<boolean>(() => visibleRowCount.value < matchingRows.value.length);

// A row that is uploading always stays on the page, even when the filters or paging would leave it out.
// Taking it off the page unmounts its upload form, which aborts the files that haven't gone up yet.
const renderedRows = computed<Array<LocationUploadRowData>>(() => {
  const pageKeys = new Set(matchingRows.value.slice(0, visibleRowCount.value).map(row => row.key));
  return orderedRows.value.filter(row => pageKeys.has(row.key) || uploadingRowKeys.value.has(row.key));
});

const emptyText = computed<string>(() => {
  if (orderedRows.value.length === 0) return "No locations or events yet. Add one to upload photos for it, or start an album.";
  return "Nothing matches these filters. Add it as a new location, or start an album.";
});

const loadMoreSentinel = ref<HTMLElement | null>(null);
let loadMoreObserver: IntersectionObserver | null = null;

// The observer only reports changes, so it is rebuilt after each page: a sentinel that is still on screen
// once the new rows are in would otherwise never fire again.
function observeLoadMoreSentinel() {
  loadMoreObserver?.disconnect();
  loadMoreObserver = null;
  const element = loadMoreSentinel.value;
  if (!element || typeof IntersectionObserver === "undefined") return;
  loadMoreObserver = new IntersectionObserver(
    async entries => {
      if (!entries.some(entry => entry.isIntersecting) || !hasMoreRows.value) return;
      visibleRowCount.value += ROWS_PER_PAGE;
      await nextTick();
      observeLoadMoreSentinel();
    },
    { rootMargin: "200px" }
  );
  loadMoreObserver.observe(element);
}

watch(loadMoreSentinel, observeLoadMoreSentinel);
onBeforeUnmount(() => loadMoreObserver?.disconnect());

watch([search, selectedCategory, photosFilter], async () => {
  visibleRowCount.value = ROWS_PER_PAGE;
  await nextTick();
  observeLoadMoreSentinel();
});

// Selecting photos to move or delete ========================================
const isSelectMode = ref<boolean>(false);
const checkedPhotoIds = ref<Array<string>>([]);
const isDeletePhotosModalVisible = ref<boolean>(false);
const isMovePhotosModalVisible = ref<boolean>(false);

const checkedPhotoIdSet = computed<Set<string>>(() => new Set(checkedPhotoIds.value));

function checkedPhotoIdsFor(row: LocationUploadRowData): Array<string> {
  if (!isSelectMode.value || checkedPhotoIds.value.length === 0) return NO_PHOTO_IDS;
  return row.photoIds.filter(photoId => checkedPhotoIdSet.value.has(photoId));
}

function handleCheckUncheckFile(photoId: string, isChecked: boolean) {
  if (isChecked) {
    if (!checkedPhotoIdSet.value.has(photoId)) checkedPhotoIds.value.push(photoId);
  } else {
    checkedPhotoIds.value = checkedPhotoIds.value.filter(id => id !== photoId);
  }
}

function handleCheckUncheckAll(row: LocationUploadRowData, isChecked: boolean) {
  const rowPhotoIds = new Set(row.photoIds);
  const others = checkedPhotoIds.value.filter(photoId => !rowPhotoIds.has(photoId));
  checkedPhotoIds.value = isChecked ? [...others, ...row.photoIds] : others;
}

// Moving, deleting and editing an album can change which gallery a row stands for (ex. deleting a location's oldest
// album hands its row to the next one). Doing that under a row that is mid-upload would send its remaining photos
// to the wrong place, so these wait until nothing is uploading. Uploads are likewise off while selecting.
function startSelecting() {
  if (isAnyUploading.value) return;
  isSelectMode.value = true;
}

function stopSelecting() {
  isSelectMode.value = false;
  checkedPhotoIds.value = [];
}

// SelectedFilesPanel takes upload-form images; only the id matters for a photo that is already saved.
const selectedFiles = computed<Array<UploadedImage>>(() =>
  checkedPhotoIds.value.map(photoId => ({ name: photoId, size: null, pending: false, active: false, serverId: photoId, uploadFileName: null, finishedUploading: true, blob: null, type: "" }))
);

function handlePhotosDeleted() {
  const deleted = checkedPhotoIdSet.value;
  galleries.value.forEach(gallery => {
    if (gallery.photoIds?.some(photoId => deleted.has(photoId))) gallery.photoIds = gallery.photoIds.filter(photoId => !deleted.has(photoId));
  });
  isDeletePhotosModalVisible.value = false;
  stopSelecting();
  emitGalleries();
}

const moveTargets = computed<Array<{ name: string; id: string }>>(() =>
  orderedRows.value.map(row => {
    const locationLabel = rowLocationLabel(row);
    return { name: locationLabel ? `${row.title} (${locationLabel})` : row.title, id: row.key };
  })
);

async function resolveMoveTarget(rowKey: string): Promise<string> {
  return (await ensureGalleryForRow(rowKey)).id;
}

async function handlePhotosMoved() {
  isMovePhotosModalVisible.value = false;
  stopSelecting();
  // The server took the photos out of every other gallery (and made the new album when that was picked), so reload.
  await reloadGalleries();
}

// Adding a location, event or album =========================================
// One modal for all three (AddLocationOrAlbumModal). A Location or Event only becomes a row here: its album is still
// created by the first upload (ensureGalleryForRow).
const sectionRef = ref<HTMLElement | null>(null);
const isAddModalVisible = ref<boolean>(false);
const addModalInitialType = ref<AddLocationOrAlbumType>("Location");

function openAddModal(type: AddLocationOrAlbumType) {
  addModalInitialType.value = type;
  isAddModalVisible.value = true;
}

/** `location` is new, or one they already had and picked with "Use the saved one". Either way its row is "loc:{id}". */
function handleLocationAdded({ location }: { location: LocationData }) {
  // Added to the list here rather than by reloading it, so the row shows even if that request were to fail.
  upsertLocation(location);
  void revealRow(`loc:${location.id}`);
}

function handleAlbumCreated(gallery: PhotoGalleryCustomerData) {
  upsertGallery(gallery);
  void revealRow(`gal:${gallery.id}`);
}

/**
 * Puts a row the user just asked for where they can see it: on top of the list, on screen.
 * Without this, adding a Location while a filter was on that it doesn't match (ex. "With photos") looked like nothing
 * had happened, and "Use the saved one" pointed at a row that could be anywhere down a long list.
 */
async function revealRow(rowKey: string) {
  const row = rowsByKey.value.get(rowKey);
  if (!row) return;

  // Only when the filters would hide the row. Someone who searched "chest", found nothing and added "Chester's"
  // keeps their search.
  if (!rowMatchesFilters(row, filters.value)) {
    search.value = "";
    selectedCategory.value = "";
    photosFilter.value = "all";
  }

  // orderRows puts rows the page hasn't shown yet on top, so forgetting this one's place moves it there. A new row
  // has no place to forget and lands on top anyway.
  rowOrder.value = rowOrder.value.filter(key => key !== rowKey);

  await nextTick();
  const rowElement = Array.from(sectionRef.value?.querySelectorAll<HTMLElement>("[data-row-key]") ?? []).find(element => element.dataset.rowKey === rowKey);
  rowElement?.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

// Albums ====================================================================
const isAlbumEditorVisible = ref<boolean>(false);
const albumBeingEdited = ref<PhotoGalleryCustomerData | null>(null);
const shouldOpenAlbumEditorWithLocationPicker = ref<boolean>(false);
const isDeleteAlbumModalVisible = ref<boolean>(false);
const albumBeingDeleted = ref<PhotoGalleryCustomerData | null>(null);

function openAlbumEditor(gallery: PhotoGalleryCustomerData, withLocationPicker: boolean) {
  albumBeingEdited.value = gallery;
  shouldOpenAlbumEditorWithLocationPicker.value = withLocationPicker;
  isAlbumEditorVisible.value = true;
}

function handleAlbumSaved(savedGallery: PhotoGalleryCustomerData) {
  upsertGallery(savedGallery);
}

// The editor's location picker can add or change locations, whether or not the album itself gets saved.
watch(isAlbumEditorVisible, isVisible => {
  if (!isVisible) void reloadLocations();
});

async function handleDeleteAlbum(row: LocationUploadRowData) {
  if (!row.gallery) return;
  if (row.photoIds.length > 0) {
    // Show confirmation modal if there are items
    albumBeingDeleted.value = row.gallery;
    isDeleteAlbumModalVisible.value = true;
    return;
  }
  await PhotoGalleryV2Repo.deleteMyUploadsGroup(props.customerId, row.gallery.id, props.currentOperationSource);
  removeGallery(row.gallery.id);
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Location uploads ===========================================================
.location-uploads {
  max-width: calc(100dvw - 36px);

  &__heading {
    margin-bottom: 16px;
    gap: 16px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__title {
    margin: 0;
  }

  &__subtitle {
    margin: 5px 0 0;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__new-album {
    flex-shrink: 0;
  }

  &__toolbar {
    margin-bottom: 14px;
    gap: 10px;
    display: grid;
    grid-template-columns: minmax(0, 190px) 170px 170px minmax(0, 1fr);
    align-items: center;
  }

  &__search {
    height: 36px;
    box-sizing: border-box;
    padding: 0 10px;
    gap: 8px;
    display: flex;
    align-items: center;
    border: 1px solid #d8dce2;
    border-radius: 6px;
    background: #fff;

    &:focus-within {
      border-color: #087f82;
    }

    input {
      width: 100%;
      min-width: 0;
      padding: 0;
      border: 0;
      outline: 0;
      background: transparent;
      font: 400 14px/18px sans-serif;
    }
  }

  // Same look as the category dropdown next to it.
  &__photos-filter {
    width: 100%;

    :deep(.dropdown-wrapper__clickable-element) {
      width: 100%;
    }
  }

  &__filter-trigger {
    width: 100%;
    height: 36px;
    padding: 0 10px;
    gap: 7px;
    display: flex;
    align-items: center;
    border: 1px solid #d8dce2;
    border-radius: 6px;
    background: #fff;
    color: #28343b;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;

    span {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:hover {
      border-color: #087f82;
    }

    &:focus-visible {
      outline: 2px solid #087f82;
      outline-offset: 2px;
    }
  }

  &__filter-menu {
    padding: 5px;

    button {
      width: 100%;
      padding: 8px 10px;
      display: flex;
      align-items: center;
      border: 0;
      border-radius: 4px;
      background: #fff;
      color: #28343b;
      text-align: left;
      white-space: nowrap;
      cursor: pointer;

      &:hover {
        background: #eef6f5;
      }

      &:focus-visible {
        outline: 2px solid #087f82;
        outline-offset: -2px;
      }
    }
  }

  &__filter-menu &__filter-choice--selected {
    background: #eef6f5;
  }

  &__sort {
    justify-self: end;
    white-space: nowrap;
  }

  &__rows-wrap {
    position: relative;
  }

  &__rows {
    margin: 0;
    padding: 0;
    gap: 10px;
    display: flex;
    flex-direction: column;
    list-style: none;
  }

  &__empty {
    padding: 28px;
    border: 1px dashed #d8dce2;
    border-radius: 9px;
    background: #f7f9fa;
    text-align: center;
  }

  &__empty-text {
    margin: 0 0 14px;
    color: #667085;
  }

  &__empty-actions {
    gap: 10px;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
  }

  &__more {
    padding: 30px;
    text-align: center;
    color: #667085;
    font: 14px/18px sans-serif;
  }

  &__selected-files-panel {
    width: 100%;
    margin-top: 14px;
    position: sticky !important;
    inset: auto auto 0 0;
    z-index: 4;
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .location-uploads {
    &__heading {
      flex-wrap: wrap;
    }

    &__toolbar {
      grid-template-columns: minmax(0, 1fr);
    }

    &__sort {
      justify-self: start;
    }
  }
}
</style>
